import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import tablemaintService, { type AddableColumn } from '../../services/tablemaint.service';
import userSettingsService from '../../services/userSettings.service';
import { getApiFieldName } from '../../utils/dynamicColumns';
import { titleCase } from '../../utils/titleCase';
import type { TablemaintField } from '../../config/dataTables.registry';

/**
 * The tablemaint grid's dynamic-column picker.
 *
 * A table's grid renders only the columns its registry declares, while the backend
 * whitelist is a strictly larger set — roughly 400 selectable columns across the 94
 * engine tables are never shown, so a question like "which EDI partners auto-accept?"
 * had no answer in the UI even though `auto_accept` was already fetched by the edit
 * drawer. This hook publishes that universe to TanTable's column-insert picker and
 * remembers what each user added.
 *
 * Two contracts it has to bridge:
 *
 *  1. PERSISTENCE. TanTable saves column visibility/order/sizing under its
 *     `settingsKey`, but deliberately NOT `additionalFields` — that set is the host's,
 *     because the host is what fetches the data. Without saving it here, an added
 *     column vanishes on reload.
 *
 *  2. THE DYNAMIC-COLUMN NAME MAPPING. TanTable speaks the dispatch dashboard's
 *     FIELD_NAME_MAPPING: a column's id is getApiFieldName(dbName), and what comes
 *     back from the picker is getDbFieldName(columnId). Seven names round-trip
 *     through that map and several are real tablemaint columns — `billtoname`,
 *     `customername` and `region` on six tables, plus kleinschmidt_events's
 *     `deliverydate`, which is the API name of `d_arrivaldatetime` and so maps DOWN
 *     to a name that table doesn't have. The backend then drops it and the column
 *     renders permanently empty with no error anywhere. `reconcile` undoes the
 *     mapping whenever the mapped name isn't a real column of this table but the
 *     unmapped one is, and `colId` carries the mapped id back the other way.
 */

export interface GridColumnPicker {
  /** Fed to TanTable's insert picker — DB names, minus what the grid already shows. */
  availableFields: { fieldname: string; displayname: string; type: string }[];
  /** The user's added columns (DB names). Empty until `ready`. */
  additionalFields: string[];
  /** Extra field specs to append to the grid's column list. */
  extraFields: TablemaintField[];
  /** Columns to request via `?fields=` — the added ones plus their companions. */
  requestFields: string[];
  /** Everything GET /columns said about this table, by column name. Carries the
   *  backend's authoritative `mass_edit` role as well as the display type. */
  columnMeta: Map<string, AddableColumn>;
  /** The row key behind a TanTable column id — the inverse of `colId`. TanTable
   *  hands back the column id in its callbacks, which for a picker-added column can
   *  be the renamed form (see the mapping note above). */
  dbNameFor: (columnId: string) => string;
  /** False until the saved selection has loaded — hold the first list fetch, or it
   *  runs twice (once without the extras, once with). */
  ready: boolean;
  onAdditionalFieldsChange: (fields: string[]) => void;
}

/** A column name as a human label: 'auto_accept' → 'Auto Accept'. Squashed legacy
 *  names ('perdiemdaysfree') have no word boundaries to find and stay as one word. */
export const columnLabel = (name: string): string => titleCase(name.replace(/_/g, ' '));

/** Map the backend's inferred SQL type onto a registry field type. 'datetime' has no
 *  registry equivalent — 'text' renders the backend's already-formatted
 *  'YYYY-MM-DD HH:mm' string, which the grid's date-aware sort still orders. */
const FIELD_TYPE: Record<AddableColumn['type'], TablemaintField['type']> = {
  text: 'text',
  textarea: 'text',
  number: 'number',
  date: 'date',
  datetime: 'text',
};

const settingKey = (table: string) => `tablemaint_fields_${table}`;

/** Last known selection, mirrored locally so a repeat visit doesn't hold the grid
 *  behind a settings round-trip. The server row stays authoritative. */
const readCached = (table: string): string[] | null => {
  try {
    const raw = localStorage.getItem(settingKey(table));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeCached = (table: string, fields: string[]): void => {
  try {
    localStorage.setItem(settingKey(table), JSON.stringify(fields));
  } catch {
    /* private mode / quota — the server row still has it */
  }
};

export function useGridColumnPicker(
  tableKey: string,
  fields: TablemaintField[],
  /** Columns the page renders outside the field specs (the Deleted/Active flag) —
   *  offering them again would just duplicate a column already on screen. */
  alsoShown: string[] = [],
): GridColumnPicker {
  const [addable, setAddable] = useState<AddableColumn[]>([]);
  // null = the saved selection isn't known yet (no local mirror to start from).
  const [additional, setAdditional] = useState<string[] | null>(() => readCached(tableKey));

  // Columns the grid already renders — never offered as "additional".
  const alsoShownKey = alsoShown.join(',');
  const shownKeys = useMemo(
    () => new Set([
      ...fields.filter((f) => f.inTable !== false).map((f) => f.key),
      ...alsoShown,
    ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields, alsoShownKey],
  );
  const specByKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);

  useEffect(() => {
    const ctrl = new AbortController();
    setAddable([]);
    tablemaintService
      .columns(tableKey, ctrl.signal)
      .then((cols) => { if (!ctrl.signal.aborted) setAddable(cols); })
      .catch(() => { /* picker just offers nothing extra */ });
    return () => ctrl.abort();
  }, [tableKey]);

  useEffect(() => {
    let cancelled = false;
    const cached = readCached(tableKey);
    setAdditional(cached);
    userSettingsService
      .getUserSetting(settingKey(tableKey))
      .then((s) => {
        if (cancelled) return;
        const saved = Array.isArray(s?.setting_value) ? (s!.setting_value as string[]) : [];
        writeCached(tableKey, saved);
        // Only disturb the grid when the server actually disagrees with the mirror
        // (the selection was changed on another device).
        if (!cached || cached.join(',') !== saved.join(',')) setAdditional(saved);
      })
      .catch(() => { if (!cancelled && cached === null) setAdditional([]); });
    return () => { cancelled = true; };
  }, [tableKey]);

  const addableByName = useMemo(() => new Map(addable.map((c) => [c.name, c])), [addable]);

  // Undo the dynamic-column name mapping for anything it turned into a non-column
  // (see the class doc), and drop names this table doesn't have at all.
  const knownRef = useRef(addableByName);
  knownRef.current = addableByName;
  const reconcile = useCallback((names: string[]): string[] => {
    const known = knownRef.current;
    if (known.size === 0) return names;   // metadata not in yet — trust the caller
    const out: string[] = [];
    for (const name of names) {
      if (known.has(name)) out.push(name);
      else if (known.has(getApiFieldName(name))) out.push(getApiFieldName(name));
    }
    return out;
  }, []);

  const additionalFields = useMemo(
    () => (additional === null ? [] : reconcile(additional)),
    [additional, reconcile],
  );
  const additionalKey = additionalFields.join(',');

  const onAdditionalFieldsChange = useCallback(
    (next: string[]) => {
      const clean = reconcile(next);
      setAdditional(clean);
      writeCached(tableKey, clean);
      userSettingsService.saveUserSetting(settingKey(tableKey), clean).catch((err) => {
        console.error('Failed to save tablemaint column selection:', err);
      });
    },
    [tableKey, reconcile],
  );

  const availableFields = useMemo(
    () =>
      addable
        // `addable === false` is a column published only so the grid knows whether it
        // may be bulk-edited — its table's grid is a fixed hand-written SELECT, so
        // adding it would render an empty column. Undefined on an older backend.
        .filter((c) => c.addable !== false && !shownKeys.has(c.name))
        .map((c) => ({
          fieldname: c.name,
          // A registry spec exists for columns the drawer shows but the grid doesn't
          // (`inTable: false`) — its label is the curated one.
          displayname: specByKey.get(c.name)?.label ?? columnLabel(c.name),
          type: c.type,
        })),
    [addable, shownKeys, specByKey],
  );

  const extraFields = useMemo<TablemaintField[]>(
    () =>
      additionalFields
        .filter((name) => !shownKeys.has(name))
        .map((name) => {
          const spec = specByKey.get(name);
          const meta = addableByName.get(name);
          const base: TablemaintField = spec
            ? { ...spec, inTable: true }
            : {
                key: name,
                label: columnLabel(name),
                type: meta ? FIELD_TYPE[meta.type] : 'text',
                ...(meta?.type === 'number' ? { align: 'right' as const } : {}),
              };
          // TanTable identifies the column by its MAPPED name; the row key stays the
          // DB name. Equal for every name outside FIELD_NAME_MAPPING.
          const colId = getApiFieldName(name);
          return colId === name ? base : { ...base, colId };
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [additionalKey, shownKeys, specByKey, addableByName],
  );

  // Ask for the added columns plus any companion column their spec renders — an
  // `idKey`/`refNameKey`/`deletedKey` that isn't fetched shows as a silently missing
  // recno or an unflagged deleted reference.
  const requestFields = useMemo(() => {
    const want = new Set<string>();
    for (const f of extraFields) {
      want.add(f.key);
      for (const companion of [f.idKey, f.refNameKey, f.deletedKey]) {
        if (companion) want.add(companion);
      }
    }
    return [...want];
  }, [extraFields]);

  const dbNameFor = useCallback(
    (columnId: string) => extraFields.find((f) => f.colId === columnId)?.key ?? columnId,
    [extraFields],
  );

  return {
    availableFields,
    additionalFields,
    extraFields,
    requestFields,
    columnMeta: addableByName,
    dbNameFor,
    ready: additional !== null,
    onAdditionalFieldsChange,
  };
}

export default useGridColumnPicker;
